import { useT } from "@/i18n/I18nProvider";
import type { RouteKey } from "@/lib/routes";
import { imageFor } from "@/lib/imagery";
import { Breadcrumbs } from "./Breadcrumbs";

interface PageHeroProps {
  routeKey: RouteKey;
  imageUrl?: string;
  imageAlt?: string;
  showBreadcrumbs?: boolean;
  compact?: boolean;
}

/**
 * Editorial split hero used across detail / index pages.
 * Left: eyebrow + huge display title + short lede.
 * Right: optional editorial image, framed hard-edged.
 */
export function PageHero({
  routeKey,
  imageUrl,
  imageAlt,
  showBreadcrumbs = true,
  compact = false,
}: PageHeroProps) {
  const { t } = useT();
  const meta = t.routes[routeKey] as { title: string; eyebrow?: string; lede?: string };

  return (
    <section
      className={
        "border-b border-bone-500 " +
        (compact ? "pt-24 md:pt-28 pb-12 md:pb-16" : "pt-28 md:pt-32 pb-16 md:pb-24")
      }
      data-testid={`page-hero-${routeKey.replace(".", "-")}`}
    >
      <div className="container-bnm">
        {showBreadcrumbs && (
          <div className="mb-10 reveal-on-mount">
            <Breadcrumbs current={routeKey} />
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
          <div className={imageUrl ? "lg:col-span-7" : "lg:col-span-9"}>
            {meta.eyebrow && (
              <p className="eyebrow mb-6 reveal-on-mount">{meta.eyebrow}</p>
            )}
            <h1 className="font-display font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-ink reveal-on-mount reveal-delay-1">
              {meta.title}
            </h1>
            {meta.lede && (
              <p className="mt-6 md:mt-8 text-lg md:text-xl text-slate2 max-w-prose-tight leading-relaxed reveal-on-mount reveal-delay-2">
                {meta.lede}
              </p>
            )}
          </div>
          {imageUrl && (
            <div className="lg:col-span-5 reveal-on-mount reveal-delay-3">
              <div className="aspect-[4/5] w-full overflow-hidden bg-ink/5">
                <img
                  src={imageUrl}
                  srcSet={imageFor(routeKey)?.srcSet}
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  alt={imageAlt ?? meta.title}
                  className="w-full h-full object-cover"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
